'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { FC, PropsWithChildren, useState } from 'react';

import Modal from '../Modal';
import Button from '../Button';

import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import XIcon from '../Icons/X';
import { FaRegUserCircle } from 'react-icons/fa';
import { Title, Description } from '@radix-ui/react-dialog';

// Form only renders inside the open modal
const LoginForm = dynamic(() => import('../LoginForm'), { ssr: false });

interface LoginModalProps extends PropsWithChildren {
  dict: IModalsDictionary;
  text?: boolean;
  className?: string;
  isHeader?: boolean;
}

const LoginModal: FC<LoginModalProps> = ({
  dict,
  text = false,
  className,
  isHeader = false,
  children,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const searchParams = useSearchParams();

  const openLogin = !!searchParams.get('openLogin');
  const [prevOpenLogin, setPrevOpenLogin] = useState(false);
  // Opens once per ?openLogin in the URL; closing it keeps it closed until the param comes back
  if (openLogin !== prevOpenLogin) {
    setPrevOpenLogin(openLogin);
    if (openLogin) setModalIsOpen(true);
  }

  function renderModalTrigger() {
    if (children) {
      return children;
    }

    if (text) {
      return (
        <button className="appearance-none outline-none text-primary text-lg font-averta-semibold cursor-pointer">
          {isHeader ? dict.loginModal.login : dict.loginModal.title}
        </button>
      );
    }

    return (
      <Button
        variant="outline"
        className={
          className
            ? className
            : 'h-10 w-10 px-0 desktop:text-gray-900 bg-[#EBBF20] hover:bg-white border-[#EBBF20] transition-colors group'
        }>
        {/* {isHeader ? dict.loginModal.login : dict.loginModal.title} */}
        {modalIsOpen ? (
          <XIcon className="h-4 w-4 text-[#E4B321]" />
        ) : (
          <FaRegUserCircle
            size={16}
            className="fill-white group-hover:fill-black transition-colors duration-200"
          />
        )}
      </Button>
    );
  }

  return (
    <Modal.Root open={modalIsOpen} onOpenChange={setModalIsOpen}>
      <Modal.Trigger>{renderModalTrigger()}</Modal.Trigger>
      <Modal.Content
        contentCustomClass="md:!w-[600px]"
        onOpenChange={setModalIsOpen}>
        <Title className="hidden" />
        <Description className="hidden" />
        <LoginForm isHeader={isHeader} dict={dict} />
      </Modal.Content>
    </Modal.Root>
  );
};

export default LoginModal;
